import { signupRoute } from './signup-diagnostics'

export const CLERK_SCRIPT_DIAGNOSTIC_EVENT = 'diagnostic:clerk_script_failure'
type Properties = Record<string, string | number | boolean>

function classify(raw: string, allowOriginOnly = false) {
  const url = new URL(raw)
  if (
    url.protocol !== 'https:' ||
    !['clerk.wwjdfilm.com', 'clerk.christspiracy.com'].includes(url.hostname)
  )
    return undefined
  const kind = url.pathname.startsWith('/npm/@clerk/ui@')
    ? 'clerk_ui'
    : url.pathname.startsWith('/npm/@clerk/clerk-js@')
      ? 'clerk_js'
      : allowOriginOnly && url.pathname === '/'
        ? 'clerk_script'
        : undefined
  return kind ? { kind, url } : undefined
}

// Observe only: never retry, suppress an error, or change a script's handlers.
export function installClerkScriptDiagnostics(browser: Window, capture: (p: Properties) => void) {
  const sent = new Set<string>()
  const report = (raw: string, stage: string) => {
    try {
      const resource = classify(raw, stage === 'csp_blocked')
      if (!resource || sent.size >= 5) return
      const key = `${resource.kind}:${stage}`
      if (sent.has(key)) return
      sent.add(key)
      let timing: PerformanceResourceTiming | undefined
      try {
        const entries = browser.performance.getEntriesByName(resource.url.href, 'resource')
        timing = entries.at(-1) as PerformanceResourceTiming | undefined
      } catch {
        // Timing is optional; still report failures on older/restricted browsers.
      }
      const status = timing?.responseStatus
      capture({
        diagnostic_version: 1,
        request_kind: resource.kind,
        stage,
        route: signupRoute(browser.location.pathname),
        online: browser.navigator.onLine,
        visibility: browser.document.visibilityState === 'visible' ? 'visible' : 'hidden',
        timing_present: !!timing,
        duration_ms: timing ? Math.round(timing.duration) : -1,
        // Zero can mean unavailable cross-origin data, not an HTTP failure.
        status: typeof status === 'number' && status > 0 ? status : -1,
      })
    } catch {
      // Optional diagnostics must not interfere with authentication.
    }
  }
  const onError = (event: Event) => {
    const target = event.target as HTMLScriptElement | null
    if (target?.tagName === 'SCRIPT') report(target.src, 'script_error')
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    try {
      const message = typeof event.reason?.message === 'string' ? event.reason.message : ''
      const match = message.match(/\((error|timeout|missing): (https:\/\/[^\s)]+)\)/)
      if (match) report(match[2], `chunk_${match[1]}`)
    } catch {
      // Some third-party rejection reasons have throwing property getters.
    }
  }
  const onPolicy = (event: SecurityPolicyViolationEvent) => {
    // Report-only policies do not block execution.
    if (event.disposition === 'enforce' && event.effectiveDirective.startsWith('script-src'))
      report(event.blockedURI, 'csp_blocked')
  }
  browser.addEventListener('error', onError, true)
  browser.addEventListener('unhandledrejection', onRejection)
  browser.addEventListener('securitypolicyviolation', onPolicy)
  return () => {
    browser.removeEventListener('error', onError, true)
    browser.removeEventListener('unhandledrejection', onRejection)
    browser.removeEventListener('securitypolicyviolation', onPolicy)
  }
}
